"use client";

import { init, useRawInitData, isTMA } from "@tma.js/sdk-react";
import {
	createContext,
	useContext,
	ReactNode,
	useEffect,
	useState,
    useCallback,
} from "react";
import { useApp } from "./AppProvider";
import { User } from "@/types/dto/user";

interface TelegramContextValue {
	rawInitData?: string;
	user?: User | null;
    serverTimeOffset: number; // Разница между временем сервера и клиента
    getServerTime: () => Date; // Функция для получения точного времени сервера
    refreshUser: () => Promise<void>;
}

const TelegramContext = createContext<TelegramContextValue>({
    serverTimeOffset: 0,
    getServerTime: () => new Date(),
    refreshUser: async () => {},
});

function TelegramProviderInner({ children }: { children: ReactNode }) {
	const { setLoading } = useApp();
	const rawInitData = useRawInitData();
	const [user, setUser] = useState<User | null>(null);
    const [serverTimeOffset, setServerTimeOffset] = useState<number>(0);

    const fetchUser = useCallback(async () => {
        if (rawInitData) {
            try {
                // Засекаем время до отправки запроса
                const requestTime = Date.now();
                
                const response = await fetch(
                    "/auth/methods/providers/telegram",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ rawInitData }),
                    },
                );
                
                // Засекаем время после получения ответа
                const responseTime = Date.now();
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setUser(data.user);
                        
                        // Вычисляем точное время на основе ответа сервера
                        if (data.serverTime) {
                            const serverTime = new Date(data.serverTime).getTime();
                            // Предполагаем, что сервер обработал запрос ровно посередине между отправкой и получением
                            const estimatedClientTimeWhenServerReplied = requestTime + (responseTime - requestTime) / 2;
                            // Разница: насколько серверные часы спешат (+) или отстают (-) от клиентских
                            const offset = serverTime - estimatedClientTimeWhenServerReplied;
                            setServerTimeOffset(offset);
                        }
                    } else {
                        console.error("Auth failed:", data.error);
                    }
                } else {
                    console.error("Auth request failed:", response.status);
                }
            } catch (e) {
                console.error("Telegram auth error:", e);
            }
        }
    }, [rawInitData]);

	useEffect(() => {
		const auth = async () => {
			await fetchUser();
			setLoading(false);
		};
		auth();
	}, [fetchUser, setLoading]);

    // Функция для получения текущего синхронизированного времени
    const getServerTime = useCallback(() => {
        return new Date(Date.now() + serverTimeOffset);
    }, [serverTimeOffset]);

	return (
		<TelegramContext.Provider value={{ 
            rawInitData, 
            user, 
            serverTimeOffset, 
            getServerTime, 
            refreshUser: fetchUser 
        }}>
			{children}
		</TelegramContext.Provider>
	);
}

export function TelegramProvider({ children }: { children: ReactNode }) {
	const { setLoading } = useApp();
	const [sdkInitialized, setSdkInitialized] = useState(false);
	const [isTelegram, setIsTelegram] = useState(false);

	useEffect(() => {
		const initTelegram = async () => {
			try {
				const isTelegramApp = await isTMA(); 
				
				if (isTelegramApp) {
					await init();
					setIsTelegram(true);
				} else {
					setLoading(false);
				}
			} catch (e) {
				console.error("Telegram SDK init error:", e);
				setLoading(false);
			} finally {
				setSdkInitialized(true);
			}
		};
		initTelegram();
	}, [setLoading]);

	if (!sdkInitialized) {
		return null;
	}

	if (isTelegram) {
		return <TelegramProviderInner>{children}</TelegramProviderInner>;
	}

	return <>{children}</>;
}

export const useTelegram = () => {
	const context = useContext(TelegramContext);
	if (context === undefined) {
		throw new Error("useTelegram must be used within a TelegramProvider");
	}
	return context;
};

export const useRawInitDataHook = useRawInitData;